import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AccountService } from '../../../shared/account.service';
import { AccountReqDto } from '../../../shared/account.model';
import { UserService } from 'src/app/shared/user.service';

@Component({
  selector: 'app-account-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    MatExpansionModule
  ],
  templateUrl: './account-form.component.html',
  styleUrls: ['./account-form.component.css']
})
export class AccountFormComponent implements OnInit {
  accountForm: FormGroup;
  isEdit = false;
  isSubmitting = false;
  accountId: number | null = null;
  viewMode = false;

  constructor(
    private fb: FormBuilder,
    private accountService: AccountService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private userService: UserService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    // Check if we're editing, viewing, or adding an account
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.accountId = +params['id'];
        // Check if it's view mode from the route
        this.viewMode = this.route.snapshot.url.some(segment => segment.path === 'view');
        this.isEdit = !this.viewMode;
        this.loadAccount();
      }
    });
  }

  initializeForm() {
    this.accountForm = this.fb.group({
      // id: [{value: null, disabled: true}],
      name: ['', Validators.required],
      type: ['CUSTOMER', Validators.required],
      additionalId: [''],
      address: ['', Validators.required],
      city: [''],
      state: [''],
      zipCode: [''],
      country: [''],
      contactName: [''],
      contactEmail: ['', Validators.email],
      contactNumber: [''],
      contactSignatoryName: [''],
      contactSignatoryEmail: ['', Validators.email],
      sourceType: ['MANUAL', Validators.required],
      status: ['DRAFT', Validators.required],
      tenantId: [this.userService.loggedinUser?.tenantId],
      createdBy: [this.userService.loggedinUser?.id],
      updatedBy: [this.userService.loggedinUser?.id],
      overdueObligationCount: [{value: 0, disabled: true}],
      riskScore: [{value: 0, disabled: true}]
    });
  }

  loadAccount() {
    if (this.accountId) {
      this.accountService.getAccountById(this.accountId).subscribe({
        next: (response) => {
          // Map the response to form values
          const formData = {
            id: response.id,
            name: response.name,
            type: response.type,
            additionalId: response.additionalId,
            address: response.address,
            city: response.city,
            state: response.state,
            zipCode: response.zipCode,
            country: response.country,
            contactName: response.contactName,
            contactEmail: response.contactEmail,
            contactNumber: response.contactNumber,
            contactSignatoryName: response.contactSignatoryName,
            contactSignatoryEmail: response.contactSignatoryEmail,
            sourceType: response.sourceType,
            status: response.status,
            tenantId: response.tenantId,
            createdBy: response.createdBy,
            updatedBy: response.updatedBy,
            overdueObligationCount: response.overdueObligationCount,
            riskScore: response.riskScore
          };
          
          this.accountForm.patchValue(formData);
          
          // Ensure read-only fields remain disabled
          this.accountForm.get('id')?.disable();
          this.accountForm.get('overdueObligationCount')?.disable();
          this.accountForm.get('riskScore')?.disable();
          
          // Disable form if in view mode
          if (this.viewMode) {
            this.accountForm.disable();
          }
        },
        error: (error) => {
          console.error('Error loading account:', error);
        }
      });
    }
  }

  saveAccount() {
    if (this.accountForm.valid) {
      this.isSubmitting = true;
      
      // Get form value including disabled fields
      const formValue = this.accountForm.getRawValue();
      
      const accountDto: AccountReqDto = {
        ...formValue,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (this.isEdit && this.accountId) {
        delete accountDto.id;
        this.accountService.updateAccount(this.accountId, accountDto).subscribe({
          next: (response) => {
            this.isSubmitting = false;
            this.toastr.success('Account updated successfully!', 'Update Success');
            console.log('Account updated successfully:', response);
            this.goBack();
          },
          error: (error) => {
            this.isSubmitting = false;
            this.toastr.error('Failed to update account. Please try again.', 'Update Error');
            console.error('Error updating account:', error);
          }
        });
      } else {
        // Don't include ID for create operations
        delete accountDto.id;
        this.accountService.createAccount(accountDto).subscribe({
          next: (response) => {
            this.isSubmitting = false;
            this.toastr.success('Account created successfully!', 'Create Success');
            console.log('Account created successfully:', response);
            this.goBack();
          },
          error: (error) => {
            this.isSubmitting = false;
            this.toastr.error('Failed to create account. Please try again.', 'Create Error');
            console.error('Error creating account:', error);
          }
        });
      }
    } else {
      this.toastr.warning('Please fill in all required fields correctly.', 'Validation Error');
      // Mark all fields as touched to show validation errors
      this.accountForm.markAllAsTouched();
    }
  }

  activateAccount() {
    this.accountForm.patchValue({ status: 'ACTIVE' });
    this.toastr.success('Account status changed to Active!', 'Status Updated');
  }

  onStatusChange() {
    const newStatus = this.accountForm.get('status')?.value;
    if (newStatus) {
      const statusLabel = this.getStatusLabel(newStatus);
      this.toastr.info(`Account status changed to ${statusLabel}`, 'Status Updated');
    }
  }

  goBack() {
    this.router.navigate(['/app/account-master']);
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'ACTIVE': return 'ACTIVE';
      case 'DRAFT': return 'DRAFT';
      case 'INACTIVE': return 'INACTIVE';
      case 'BLACKLISTED': return 'BLACKLISTED';
      default: return 'DRAFT';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'ACTIVE': return 'Active';
      case 'DRAFT': return 'Draft';
      case 'INACTIVE': return 'Inactive';
      case 'BLACKLISTED': return 'Blacklisted';
      default: return 'Draft';
    }
  }
}